// useOtpSessionRecovery (SIM-10 / ACS-4134, SUB-TASK-2): survives browser-back and
// page-refresh on the OTP entry screen without re-sending a code.
//  - Refresh (BR-028/AC-030): the recoverable, non-PII OTP state is restored from
//    sessionStorage so the sent code stays valid and is not auto-resent. resendCount
//    is persisted so the BR-025 3-resend cap is NOT reset by a refresh.
//  - Browser-back (BR-027/AC-029): returns to the authorization screen; the sent code
//    stays valid (no resend).
//  - Only a whitelisted, non-PII subset is stored (BR-021): never the raw credential,
//    the OTP code, or an unmasked destination.
//  - SSR-safe: sessionStorage is only touched inside effects/callbacks and every access
//    is wrapped so a private-mode SecurityError falls back gracefully (never throws).
import { useCallback, useEffect } from 'react';

const STORAGE_KEY = 'otp_session_state';
// The ONLY fields persisted — recoverable, non-PII. Anything else (credential, otpCode,
// unmaskedDestination) is intentionally dropped.
const PERSIST_FIELDS = ['otpId', 'method', 'maskedDestination', 'resendCount', 'cooldownEndTime'];

/**
 * Recovery deliberately never triggers a resend — the sent code stays valid across
 * back/refresh (BR-027/BR-028), so no resend callback is needed here.
 * @param {Function} goTo - Screen navigator from useAuthFlow.
 * @returns {{ persist: Function, recover: Function, clear: Function, handleBackFromOtp: Function }}
 */
const useOtpSessionRecovery = goTo => {
    const recover = useCallback(() => {
        try {
            const raw = window.sessionStorage.getItem(STORAGE_KEY);

            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            // Storage unavailable (private mode) — treat as no recoverable state.
            return null;
        }
    }, []);

    const persist = useCallback(state => {
        const subset = {};

        PERSIST_FIELDS.forEach(field => {
            if (state && state[field] !== undefined) {
                subset[field] = state[field];
            }
        });

        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(subset));
        } catch (e) {
            // Storage unavailable — persistence is best-effort; do not throw.
        }
    }, []);

    const clear = useCallback(() => {
        try {
            window.sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            // Storage unavailable — nothing to clear; do not throw.
        }
    }, []);

    // Browser-back from OTP: return to authorization; the sent code stays valid, no resend.
    const handleBackFromOtp = useCallback(() => {
        goTo('authorization');
    }, [goTo]);

    // On mount (covers refresh recovery): if there is valid recoverable OTP state,
    // restore in place (no resend); otherwise fall back to credential entry.
    useEffect(() => {
        const recovered = recover();

        if (!recovered) {
            goTo('credential');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { persist, recover, clear, handleBackFromOtp };
};

export default useOtpSessionRecovery;
